import React from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Columns exactly as in the hotel's guest sheet (bulk-upload template too).
export const FIELDS = [
  { key: "srNo", label: "SR.NO", type: "number" },
  { key: "name", label: "NAME", type: "text", required: true },
  { key: "phone", label: "PHONE NO.", type: "tel" },
  { key: "checkInDate", label: "Guest Check-In Date", type: "date" },
  { key: "checkOutDate", label: "Guest Check-Out Date", type: "date" },
  { key: "guestStatus", label: "GUEST STATUS", type: "text", list: ["CHECKOUT", "CHECKIN", "RESERVED", "CANCELLED", "NO SHOW"] },
  { key: "stayDuration", label: "Reservation Stay Duration", type: "text" },
  { key: "roomType", label: "Room Type", type: "text" },
  { key: "room", label: "ROOM", type: "text" },
  { key: "ratePackage", label: "Rate/Package Name", type: "text" },
  { key: "averageRate", label: "Average Rate", type: "number" },
  { key: "remarks", label: "Remark's", type: "textarea" },
  { key: "date", label: "Date", type: "date" },
];

// 5, 4.9, 4.8 … 1.1, 1 — best rating first
export const RATING_OPTIONS = Array.from({ length: 41 }, (_, i) =>
  String(Math.round((5 - i / 10) * 10) / 10),
);

export const panel =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
export const field =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
export const label =
  "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500";
export const WHITE = { backgroundColor: "#fff", color: "#1f2937" };

export const errMsg = (e) => e?.response?.data?.message || "Something went wrong.";

export const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";

// "2026-10" → "Oct 2026"
export const monthLabel = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-").map(Number);
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

export const inr = (n) =>
  n == null || n === ""
    ? "—"
    : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STAR_PATH =
  "M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9l-5.2 2.7 1-5.8L1.5 7.7l5.9-.9L10 1.5z";

// Five stars filled to `value` (e.g. 3.7 → three full stars + 70% of the fourth).
export const StarRating = ({ value, size = 16, showValue = true }) => {
  if (value == null || value === "") {
    return <span className="text-xs text-gray-400">Not rated</span>;
  }
  const v = Number(value);
  return (
    <span className="inline-flex items-center gap-1.5" title={`${v} / 5`}>
      <span className="inline-flex">
        {[0, 1, 2, 3, 4].map((i) => {
          const fill = Math.max(0, Math.min(1, v - i));
          return (
            <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
              <svg width={size} height={size} viewBox="0 0 20 20" className="absolute inset-0">
                <path d={STAR_PATH} fill="#e5e7eb" />
              </svg>
              <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
                <svg width={size} height={size} viewBox="0 0 20 20">
                  <path d={STAR_PATH} fill="#f59e0b" />
                </svg>
              </span>
            </span>
          );
        })}
      </span>
      {showValue && (
        <span className="text-xs font-semibold tabular-nums text-gray-700">{v.toFixed(1)}</span>
      )}
    </span>
  );
};

// One month of the sheet → .xlsx in the same column layout (+ Rating).
export async function downloadMonthExcel(month, rows) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(monthLabel(month));
  ws.addRow([...FIELDS.map((f) => f.label), "Rating"]);
  rows.forEach((r) =>
    ws.addRow([
      ...FIELDS.map((f) =>
        f.type === "date" ? (r[f.key] ? new Date(r[f.key]) : null) : r[f.key] ?? "",
      ),
      r.rating ?? "",
    ]),
  );
  ws.getRow(1).font = { bold: true };
  ws.views = [{ state: "frozen", ySplit: 1 }];
  ws.columns.forEach((c, i) => {
    c.width = i === 1 || i === 6 || i === 7 || i === 11 ? 26 : 15;
  });
  [4, 5, 13].forEach((c) => {
    ws.getColumn(c).numFmt = "dd-mmm-yyyy";
  });
  const buf = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `hotel-feedback-${month}.xlsx`,
  );
}

// Blank .xlsx with the exact headers (plus an optional Rating column).
export async function downloadTemplate() {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("hotel leads");
  ws.addRow([...FIELDS.map((f) => f.label), "Rating"]);
  ws.addRow([
    1,
    "Guest Name",
    "9876543210",
    new Date(Date.UTC(2026, 9, 1)),
    new Date(Date.UTC(2026, 9, 2)),
    "CHECKOUT",
    "Oct 01, 2026 - Oct 02, 2026",
    "Premium Room",
    110,
    "EP",
    3752,
    "ALL GOOD",
    new Date(Date.UTC(2026, 9, 5)),
    4.5,
  ]);
  ws.getRow(1).font = { bold: true };
  ws.columns.forEach((c, i) => {
    c.width = i === 1 || i === 6 || i === 7 || i === 11 ? 26 : 16;
  });
  [4, 5, 13].forEach((c) => {
    ws.getColumn(c).numFmt = "dd-mmm-yyyy";
  });
  const buf = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    "hotel-feedback-template.xlsx",
  );
}

// Repeat guest (same phone on another row) — already called before, so don't
// call again and again. Shared look with the Feedback sheet.
export const RepeatBadge = ({ repeat }) =>
  repeat ? (
    <div
      className="mt-1 w-fit rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold text-white"
      title={`Also on ${repeat.count} other row(s)${
        repeat.lastCalledAt ? ` · last called ${fmtDate(repeat.lastCalledAt)}` : " · not called yet"
      }${repeat.lastRating != null ? ` · rated ${repeat.lastRating}★` : ""}`}
    >
      REPEAT ×{repeat.count + 1}
      {repeat.lastCalledAt ? ` · called ${fmtDate(repeat.lastCalledAt)}` : ""}
    </div>
  ) : null;
